import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
  Radio,
  RadioGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Script, Actor, CustomPhotoAvatar, AssetActor } from "../../../context/types";
import { THEME_COLORS } from "../../../context/constants";

type ActorKind = "regular" | "avatarLab" | "assetActor";

interface MultipleScriptsModalProps {
  open: boolean;
  onClose: () => void;
  scripts: Script[];
  selectedActors: Actor[];
  selectedAvatarLabActors: CustomPhotoAvatar[];
  selectedAssetActors?: AssetActor[];
  onGenerate: (selectedScripts: Script[], selectedActorId: string, actorKind: ActorKind) => Promise<void>;
  isGenerating: boolean;
}

const MultipleScriptsModal: React.FC<MultipleScriptsModalProps> = ({
  open,
  onClose,
  scripts,
  selectedActors,
  selectedAvatarLabActors,
  selectedAssetActors = [],
  onGenerate,
  isGenerating,
}) => {
  // State for selected scripts (all selected by default)
  const [selectedScriptIds, setSelectedScriptIds] = useState<string[]>([]);

  // State for selected actor
  const [selectedActorId, setSelectedActorId] = useState<string>("");
  const [actorKind, setActorKind] = useState<ActorKind>("regular");

  // Initialize with all scripts selected
  useEffect(() => {
    if (open && scripts.length > 0) {
      setSelectedScriptIds(scripts.map((s) => s._id));
    }
  }, [open, scripts]);

  // Set default actor when modal opens
  useEffect(() => {
    if (open) {
      if (selectedAvatarLabActors.length > 0) {
        setSelectedActorId(selectedAvatarLabActors[0]._id);
        setActorKind("avatarLab");
      } else if (selectedActors.length > 0) {
        setSelectedActorId(selectedActors[0].avatar_id);
        setActorKind("regular");
      } else if (selectedAssetActors.length > 0) {
        setSelectedActorId(selectedAssetActors[0]._id);
        setActorKind("assetActor");
      }
    }
  }, [open, selectedActors, selectedAvatarLabActors, selectedAssetActors]);

  const handleScriptToggle = (scriptId: string) => {
    setSelectedScriptIds((prev) =>
      prev.includes(scriptId)
        ? prev.filter((id) => id !== scriptId)
        : [...prev, scriptId]
    );
  };

  const handleSelectAllScripts = () => {
    if (selectedScriptIds.length === scripts.length) {
      setSelectedScriptIds([]);
    } else {
      setSelectedScriptIds(scripts.map((s) => s._id));
    }
  };

  const handleActorSelect = (actorId: string, kind: ActorKind) => {
    setSelectedActorId(actorId);
    setActorKind(kind);
  };

  const handleGenerate = async () => {
    const selectedScripts = scripts.filter((s) => selectedScriptIds.includes(s._id));
    await onGenerate(selectedScripts, selectedActorId, actorKind);
  };

  const handleClose = () => {
    if (!isGenerating) {
      onClose();
    }
  };

  const allActors: Array<{ id: string; name: string; kind: ActorKind; thumbnail: string }> = [
    ...selectedActors.map((a) => ({
      id: a.avatar_id,
      name: a.avatar_name,
      kind: "regular" as ActorKind,
      thumbnail: (a as any).preview_image_url || "/placeholder-avatar.png",
    })),
    ...selectedAvatarLabActors.map((a) => ({
      id: a._id,
      name: a.name,
      kind: "avatarLab" as ActorKind,
      thumbnail: (a as any).image_url_list?.[0] || "/placeholder-avatar.png",
    })),
    ...selectedAssetActors.map((a) => ({
      id: a._id,
      name: a.name,
      kind: "assetActor" as ActorKind,
      thumbnail: a.image_url || "/placeholder-avatar.png",
    })),
  ];

  const canGenerate = selectedScriptIds.length > 0 && selectedActorId && !isGenerating;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          minHeight: "500px",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            Generate Videos with Multiple Scripts
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={isGenerating}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Select the scripts you want to generate videos for and choose one actor. A video will be generated for each selected script.
        </Typography>

        <Box display="flex" gap={3} sx={{ minHeight: "350px" }}>
          {/* Left side - Scripts */}
          <Box flex={1} sx={{ borderRight: `1px solid ${THEME_COLORS.gray}`, pr: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight="bold">
                Scripts ({selectedScriptIds.length}/{scripts.length} selected)
              </Typography>
              <Button
                size="small"
                onClick={handleSelectAllScripts}
                sx={{ textTransform: "none" }}
              >
                {selectedScriptIds.length === scripts.length ? "Deselect All" : "Select All"}
              </Button>
            </Box>

            <Box
              sx={{
                maxHeight: "300px",
                overflowY: "auto",
                pr: 1,
              }}
            >
              {scripts.map((script) => (
                <Box
                  key={script._id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    p: 1.5,
                    mb: 1,
                    borderRadius: "8px",
                    border: `1px solid ${
                      selectedScriptIds.includes(script._id)
                        ? THEME_COLORS.yellow
                        : THEME_COLORS.gray
                    }`,
                    backgroundColor: selectedScriptIds.includes(script._id)
                      ? "rgba(255, 191, 0, 0.1)"
                      : "transparent",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: THEME_COLORS.yellow,
                    },
                  }}
                  onClick={() => handleScriptToggle(script._id)}
                >
                  <Checkbox
                    checked={selectedScriptIds.includes(script._id)}
                    onChange={() => handleScriptToggle(script._id)}
                    sx={{
                      p: 0,
                      mr: 1.5,
                      color: THEME_COLORS.gray,
                      "&.Mui-checked": {
                        color: THEME_COLORS.yellow,
                      },
                    }}
                  />
                  <Box flex={1}>
                    <Typography variant="body1" fontWeight="medium">
                      {script.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        mt: 0.5,
                      }}
                    >
                      {script.headline || script.body || "No content"}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Right side - Actors */}
          <Box flex={1} sx={{ pl: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              Select Actor (1 required)
            </Typography>

            {allActors.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                  backgroundColor: THEME_COLORS.lightGray,
                  borderRadius: "8px",
                }}
              >
                <Typography color="textSecondary">
                  No actors selected. Please select actors first.
                </Typography>
              </Box>
            ) : (
              <RadioGroup
                value={selectedActorId}
                onChange={(e) => {
                  const actorId = e.target.value;
                  const actor = allActors.find((a) => a.id === actorId);
                  if (actor) {
                    handleActorSelect(actorId, actor.kind);
                  }
                }}
              >
                <Box
                  sx={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    pr: 1,
                  }}
                >
                  {allActors.map((actor) => (
                    <Box
                      key={actor.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1.5,
                        mb: 1,
                        borderRadius: "8px",
                        border: `1px solid ${
                          selectedActorId === actor.id
                            ? THEME_COLORS.yellow
                            : THEME_COLORS.gray
                        }`,
                        backgroundColor:
                          selectedActorId === actor.id
                            ? "rgba(255, 191, 0, 0.1)"
                            : "transparent",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: THEME_COLORS.yellow,
                        },
                      }}
                      onClick={() => handleActorSelect(actor.id, actor.kind)}
                    >
                      <Radio
                        value={actor.id}
                        sx={{
                          p: 0,
                          mr: 1.5,
                          color: THEME_COLORS.gray,
                          "&.Mui-checked": {
                            color: THEME_COLORS.yellow,
                          },
                        }}
                      />
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          overflow: "hidden",
                          mr: 1.5,
                          backgroundColor: THEME_COLORS.gray,
                        }}
                      >
                        <img
                          src={actor.thumbnail}
                          alt={actor.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight="medium">
                          {actor.name}
                        </Typography>
                        {actor.kind === "avatarLab" && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor: "#9C27B0",
                              color: "white",
                              px: 0.5,
                              borderRadius: "4px",
                              fontSize: "10px",
                            }}
                          >
                            Avatar Lab
                          </Typography>
                        )}
                        {actor.kind === "assetActor" && (
                          <Typography
                            variant="caption"
                            sx={{
                              backgroundColor: "#00897B",
                              color: "white",
                              px: 0.5,
                              borderRadius: "4px",
                              fontSize: "10px",
                            }}
                          >
                            Image Actor
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </RadioGroup>
            )}
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={handleClose} disabled={isGenerating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!canGenerate}
          startIcon={isGenerating ? <CircularProgress size={20} /> : null}
          sx={{
            backgroundColor: THEME_COLORS.yellow,
            color: "black",
            "&:hover": {
              backgroundColor: THEME_COLORS.darkYellow,
            },
            "&:disabled": {
              backgroundColor: THEME_COLORS.gray,
            },
          }}
        >
          {isGenerating
            ? "Generating..."
            : `Start Generating (${selectedScriptIds.length} video${selectedScriptIds.length !== 1 ? "s" : ""})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MultipleScriptsModal;
